/* ══════════════════════════════════════════════════════════════
   Commodity Data Service — MCX Gold, Silver, Crude Oil
   Fetches commodity prices and provides trend analysis
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from './api-client';

export interface CommodityQuote {
    name: string;
    symbol: string;
    price: number;
    change: number;
    pChange: number;
    unit: string;
    exchange: string;
    high: number;
    low: number;
    timestamp: string;
}

export interface CommoditySummary {
    commodities: CommodityQuote[];
    goldINR: CommodityQuote | null;
    silverINR: CommodityQuote | null;
    crudeOil: CommodityQuote | null;
    naturalGas: CommodityQuote | null;
}

/** Fetch all MCX commodity prices */
export async function fetchCommodities(): Promise<CommodityQuote[]> {
    try {
        const result = await apiFetch<{ commodities: CommodityQuote[] }>('/mcx-data', { cacheTTL: 60 });
        return result.data.commodities || [];
    } catch (err) {
        console.warn('Failed to fetch commodities:', err);
        return [];
    }
}

/** Fetch commodity summary with named accessors */
export async function fetchCommoditySummary(): Promise<CommoditySummary> {
    const commodities = await fetchCommodities();

    const find = (keywords: string[]): CommodityQuote | null =>
        commodities.find(c =>
            keywords.some(k => c.name.toLowerCase().includes(k) || c.symbol.toLowerCase().includes(k))
        ) || null;

    return {
        commodities,
        goldINR: find(['gold', 'gold mini', 'goldm']),
        silverINR: find(['silver', 'silverm']),
        crudeOil: find(['crude', 'crudeoil']),
        naturalGas: find(['natural gas', 'naturalgas', 'ng']),
    };
}

/** Get commodity direction label */
export function getCommodityTrend(commodity: CommodityQuote): 'up' | 'down' | 'flat' {
    if (commodity.pChange > 0.5) return 'up';
    if (commodity.pChange < -0.5) return 'down';
    return 'flat';
}
